import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { FaRocket, FaChevronDown, FaChevronUp, FaHeart, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useChoiceList } from '../context/ChoiceListContext';
import { CUTOFF_END_YEAR } from '../constants/years';
import { SITE_CONFIG } from '../config/siteConfig';
import { toast } from 'react-toastify';
import collegeApi from '@/api/colleges';
import axios from '../api/axios';

// ── Category config ───────────────────────────────────────────────────────────
const CATEGORY_LABEL: Record<string, string> = {
  'Safe': 'High',
  'Moderate': 'Fair',
  'Reach': 'Low',
  'Near Miss': 'Just Short',
};

const CATEGORY_CONFIG: Record<string, { border: string; bg: string; pill: string; text: string }> = {
  Safe:       { border: 'border-l-green-500',  bg: 'bg-green-50',  pill: 'bg-green-100 text-green-800',  text: 'text-green-700' },
  Moderate:   { border: 'border-l-yellow-500', bg: 'bg-yellow-50', pill: 'bg-yellow-100 text-yellow-800', text: 'text-yellow-700' },
  Reach:      { border: 'border-l-red-500',    bg: 'bg-red-50',    pill: 'bg-red-100 text-red-800',    text: 'text-red-700' },
  'Near Miss':{ border: 'border-l-orange-500', bg: 'bg-orange-50', pill: 'bg-orange-100 text-orange-800', text: 'text-orange-700' },
};

const defaultCfg = { border: 'border-l-gray-300', bg: 'bg-gray-50', pill: 'bg-gray-100 text-gray-700', text: 'text-gray-600' };

// ── Lean Prediction Card ──────────────────────────────────────────────────────
const PredictionCard = ({
  prediction,
  inputType,
  userValue,
  selected,
  onCompare,
  onChoiceList,
  inChoiceList,
}: {
  prediction: any;
  inputType: 'cutoff' | 'rank';
  userValue: number;
  selected: boolean;
  onCompare: () => void;
  onChoiceList: () => void;
  inChoiceList: boolean;
}) => {
  const cfg = CATEGORY_CONFIG[prediction.category] ?? defaultCfg;
  const requiredValue = inputType === 'cutoff'
    ? prediction.cutoff.cutoff_mark
    : prediction.cutoff.closing_rank;
  // collegeDiff = how much the college's threshold differs from the student's value
  // positive = college's value is above the student's value
  // negative = college's value is below the student's value
  const collegeDiff = requiredValue - userValue;
  // Does the student qualify? cutoff: yes if college needs ≤ student. rank: yes if closing rank ≥ student's rank.
  const qualifies = inputType === 'cutoff' ? collegeDiff <= 0 : collegeDiff >= 0;

  // College name: strip address after first comma if long, but preserve Anna University campus identifier
  const rawName = prediction.college.display_name || prediction.college.college_name;
  const campusMatch = rawName.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
  const displayName = campusMatch
    ? `${campusMatch[1].split(',')[0].trim()} — ${campusMatch[2]}`
    : rawName.length > 65
      ? rawName.substring(0, rawName.indexOf(',') > 20 ? rawName.indexOf(',') : 65)
      : rawName;

  return (
    <div className={`border-l-4 ${cfg.border} bg-white border border-l-0 border-gray-100 rounded-r-lg rounded-bl-lg shadow-sm hover:shadow-md transition-shadow`}>
      {/* Top row: college name + category badge */}
      <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/colleges/${prediction.college.college_code}`}
            className="font-semibold text-gray-800 text-sm md:text-base hover:text-blue-700 hover:underline leading-snug"
          >
            {displayName}
          </Link>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-500">{prediction.college.location}</span>
            {prediction.college.college_type === 'Government' && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600">Govt</span>
            )}
          </div>
          <div className="mt-1">
            <span className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded">
              {prediction.course.branch_name}
            </span>
          </div>
        </div>
        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.pill}`}>
          {CATEGORY_LABEL[prediction.category] ?? prediction.category}
        </span>
      </div>

      {/* Bottom row: rank/cutoff · diff · compare — full width */}
      <div className="px-4 pb-3 pt-2 border-t border-gray-50 flex items-center justify-between gap-4">
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm font-bold text-gray-800">
            {inputType === 'cutoff' ? requiredValue : `Rank ${requiredValue?.toLocaleString()}`}
          </span>
          <span className="text-xs text-gray-400">
            {inputType === 'cutoff' ? 'cutoff needed' : 'closing rank'}
          </span>
        </div>

        {collegeDiff !== null && collegeDiff !== undefined && (
          <span className={`text-xs font-semibold ${qualifies ? 'text-green-600' : 'text-orange-600'}`}>
            {collegeDiff > 0
              ? `+${collegeDiff.toFixed(1)} above yours`
              : collegeDiff < 0
                ? `${Math.abs(collegeDiff).toFixed(1)} below yours`
                : 'matches yours'}
          </span>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onChoiceList}
            title={inChoiceList ? 'In ChoiceList' : 'Add to ChoiceList'}
            className={`p-1.5 rounded-full border transition-colors ${inChoiceList ? 'bg-green-100 border-green-300 text-green-700' : 'bg-white border-gray-200 text-gray-400 hover:border-blue-400 hover:text-blue-600'}`}
          >
            {inChoiceList ? <FaCheck className="text-xs" /> : <FaHeart className="text-xs text-red-500" />}
          </button>
          <label className="flex items-center gap-1.5 cursor-pointer bg-white border border-gray-200 px-2.5 py-1 rounded-full hover:border-blue-400 transition-colors">
            <input
              type="checkbox"
              checked={selected}
              onChange={onCompare}
              className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded"
            />
            <span className="text-xs font-medium text-gray-600">Compare</span>
          </label>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CollegePredictor = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToChoiceList, isInChoiceList } = useChoiceList();
  const [inputType, setInputType] = useState<'cutoff' | 'rank'>(
    searchParams.get('rank') ? 'rank' : 'cutoff'
  );
  const [branchFilters, setBranchFilters] = useState<string[]>([]);
  const toggleBranch = (branch: string) =>
    setBranchFilters(prev =>
      prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]
    );
  const branchScrollRef = useRef<HTMLDivElement>(null);
  const scrollBranches = (dir: 'left' | 'right') => {
    branchScrollRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };
  const [formData, setFormData] = useState({
    cutoff: searchParams.get('cutoff') || '',
    rank: searchParams.get('rank') || '',
    community: searchParams.get('community') || 'OC',
    department: searchParams.get('branch') || '',
    districts: searchParams.get('districts')?.split(',').filter(Boolean) || [] as string[],
  });
  const [cutoffError, setCutoffError] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [districts, setDistricts] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState<number[]>([]);
  const [visibleQualified, setVisibleQualified] = useState(20);
  const [visibleNearMiss, setVisibleNearMiss] = useState(10);

  useEffect(() => {
    const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

    const readCache = (key: string) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts > CACHE_TTL) return null;
        return data;
      } catch { return null; }
    };
    const writeCache = (key: string, data: any) => {
      try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
    };

    // Use cached lists immediately for instant render
    const cachedDistricts = readCache('cache:districts');
    const cachedBranches = readCache('cache:branches');
    if (cachedDistricts) setDistricts(cachedDistricts);
    if (cachedBranches) setBranches(cachedBranches);

    // Fetch fresh in parallel, update if changed
    Promise.all([
      axios.get('/districts').catch(() => null),
      axios.get('/courses/branches/distinct').catch(() => null),
    ]).then(([dRes, bRes]: any[]) => {
      if (dRes?.data?.districts) {
        setDistricts(dRes.data.districts);
        writeCache('cache:districts', dRes.data.districts);
      }
      if (bRes?.branches) {
        setBranches(bRes.branches);
        writeCache('cache:branches', bRes.branches);
      }
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const predictMutation = useMutation({
    mutationFn: (data: any) => collegeApi.predict(data),
    onSuccess: () => toast.success('Predictions generated!'),
    onError: () => toast.error('Failed to generate predictions. Please try again.'),
  });

  const runPrediction = (silent = false) => {
    setVisibleQualified(20);
    setVisibleNearMiss(10);

    if (inputType === 'cutoff') {
      if (!formData.cutoff) { if (!silent) toast.error('Please enter your cutoff mark'); return; }
      const v = parseFloat(formData.cutoff);
      if (v < 0 || v > 200) { setCutoffError('Cutoff mark must be between 0 and 200'); return; }
    } else {
      if (!formData.rank) { if (!silent) toast.error('Please enter your rank'); return; }
    }

    const payload: any = { input_type: inputType, community: formData.community };
    if (inputType === 'cutoff') payload.cutoff = parseFloat(formData.cutoff);
    else payload.rank = parseInt(formData.rank);
    if (formData.department) payload.branch_preference = formData.department;
    if (formData.districts.length > 0) payload.districts = formData.districts;

    // Sync to URL so the result is shareable
    const params = new URLSearchParams();
    if (inputType === 'cutoff') params.set('cutoff', formData.cutoff);
    else params.set('rank', formData.rank);
    params.set('community', formData.community);
    if (formData.department) params.set('branch', formData.department);
    if (formData.districts.length > 0) params.set('districts', formData.districts.join(','));
    setSearchParams(params, { replace: true });

    predictMutation.mutate(payload);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runPrediction();
  };

  // Auto-run prediction when URL has cutoff/rank params (shared link)
  const hasUrlParams = !!(searchParams.get('cutoff') || searchParams.get('rank'));
  useEffect(() => {
    if (hasUrlParams && !predictMutation.data && !predictMutation.isLoading) {
      runPrediction(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'cutoff') {
      const v = parseFloat(value);
      setCutoffError(value && (v < 0 || v > 200) ? 'Cutoff mark must be between 0 and 200' : '');
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleDistrictChange = (id: string) => {
    setFormData(prev => ({
      ...prev,
      districts: prev.districts.includes(id) ? prev.districts.filter(d => d !== id) : [...prev.districts, id],
    }));
  };

  const handleCompareToggle = (collegeId: number) => {
    setSelectedForComparison(prev => {
      if (prev.includes(collegeId)) return prev.filter(id => id !== collegeId);
      if (prev.length >= 5) { toast.warning('You can compare up to 5 colleges'); return prev; }
      return [...prev, collegeId];
    });
  };

  const handleCompare = () => {
    if (selectedForComparison.length < 2) { toast.error('Select at least 2 colleges to compare'); return; }
    const params = new URLSearchParams({ colleges: selectedForComparison.join(','), community: formData.community, input_type: inputType });
    if (inputType === 'cutoff') params.append('cutoff', formData.cutoff);
    else params.append('rank', formData.rank);
    if (formData.department) params.append('branch', formData.department);
    navigate(`/cutoffs?${params.toString()}`);
  };

  const userValue = inputType === 'cutoff' ? parseFloat(formData.cutoff) : parseInt(formData.rank);

  return (
    <>
      <Helmet>
        <title>{`TNEA ${SITE_CONFIG.year} College Predictor — Find Colleges by Your Cutoff Mark or Rank | ChooseMyCollege`}</title>
        <meta name="description" content={`Enter your TNEA ${SITE_CONFIG.year} cutoff mark or rank to instantly see which Tamil Nadu engineering colleges you qualify for, based on the latest ${CUTOFF_END_YEAR} cutoff data. Filter by community (OC, BC, MBC, SC), branch and district.`} />
        <meta name="keywords" content={`TNEA ${SITE_CONFIG.year} college predictor, TNEA rank predictor, TNEA cutoff mark ${SITE_CONFIG.year}, which college for my TNEA rank, Tamil Nadu engineering college predictor`} />
        <meta property="og:title" content={`TNEA ${SITE_CONFIG.year} College Predictor — Find Colleges by Mark or Rank | ChooseMyCollege`} />
        <meta property="og:description" content={`Enter your TNEA ${SITE_CONFIG.year} cutoff mark or rank and instantly see which Tamil Nadu engineering colleges you qualify for. Free tool.`} />
        <meta property="og:url" content="https://choosemycollege.in/predictor" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://choosemycollege.in/predictor" />
      </Helmet>

      <div className="max-w-6xl mx-auto">


        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">College Predictor</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Form ── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
              <h2 className="text-lg font-bold mb-5 text-gray-800">Your Details</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Toggle */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Predict By</label>
                  <div className="flex rounded-lg overflow-hidden border border-gray-200">
                    {(['cutoff', 'rank'] as const).map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => { setInputType(type); setCutoffError(''); }}
                        className={`flex-1 py-2 text-sm font-medium transition-colors ${
                          inputType === type ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {type === 'cutoff' ? 'Cutoff Mark' : 'Rank'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Value input */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    {inputType === 'cutoff' ? 'Your Cutoff Mark' : 'Your TNEA Rank'}
                  </label>
                  <input
                    type="number"
                    name={inputType}
                    value={inputType === 'cutoff' ? formData.cutoff : formData.rank}
                    onChange={handleChange}
                    placeholder={inputType === 'cutoff' ? 'e.g. 180.5' : 'e.g. 5000'}
                    step={inputType === 'cutoff' ? '0.01' : '1'}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      cutoffError ? 'border-red-400' : 'border-gray-200'
                    }`}
                    required
                  />
                  {cutoffError && <p className="text-xs text-red-600 mt-1">{cutoffError}</p>}
                </div>

                {/* Community */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Community</label>
                  <select
                    name="community"
                    value={formData.community}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="OC">OC — Open Category</option>
                    <option value="BC">BC — Backward Class</option>
                    <option value="BCM">BCM — Backward Class Muslim</option>
                    <option value="MBC">MBC — Most Backward Class</option>
                    <option value="SC">SC — Scheduled Caste</option>
                    <option value="SCA">SCA — Scheduled Caste Arunthathiyar</option>
                    <option value="ST">ST — Scheduled Tribe</option>
                  </select>
                </div>

                {/* Advanced Filters */}
                <div className="border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="flex items-center justify-between w-full text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-blue-600 transition-colors"
                  >
                    <span>Optional Filters</span>
                    {showAdvancedFilters ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  {showAdvancedFilters && (
                    <div className="mt-3 space-y-4">
                      {/* Department */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Department</label>
                        <select
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          <option value="">All Departments</option>
                          {branches
                            .filter((b: any) => !['Architecture', 'Design', 'Planning'].includes(b.category))
                            .map((b: any) => (
                              <option key={b.branch_code} value={b.branch_code}>{b.display_name}</option>
                            ))}
                        </select>
                      </div>

                      {/* Districts — pill toggles */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Districts</label>
                          {formData.districts.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setFormData(p => ({ ...p, districts: [] }))}
                              className="text-xs text-blue-600 hover:text-blue-700"
                            >
                              Clear ({formData.districts.length})
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {districts.map((d: any) => {
                            const sel = formData.districts.includes(d.id.toString());
                            return (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() => handleDistrictChange(d.id.toString())}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                  sel
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                                }`}
                              >
                                {d.name}
                              </button>
                            );
                          })}
                        </div>
                        {formData.districts.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1.5">{formData.districts.length} selected</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={predictMutation.isPending || cutoffError !== ''}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {predictMutation.isPending ? 'Predicting…' : 'Predict Colleges'}
                </button>
              </form>
            </div>
          </div>

          {/* ── Results ── */}
          <div className="lg:col-span-2">
            {predictMutation.isPending && (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
                <p className="text-gray-500 text-sm">Analysing your options…</p>
              </div>
            )}

            {predictMutation.isSuccess && predictMutation.data && (() => {
              const responseData = (predictMutation.data as any).data || (predictMutation.data as any);
              const allQualified: any[] = responseData.qualified || responseData.predictions || [];
              const allNearMiss: any[] = responseData.near_miss || [];

              // Branch filter
              const uniqueBranches = Array.from(new Set(allQualified.map((p: any) => p.course.branch_name))) as string[];
              const qualified = branchFilters.length > 0 ? allQualified.filter((p: any) => branchFilters.includes(p.course.branch_name)) : allQualified;
              const nearMiss = branchFilters.length > 0 ? allNearMiss.filter((p: any) => branchFilters.includes(p.course.branch_name)) : allNearMiss;

              return (
                <>
                  {/* Summary bar */}
                  <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-4 mb-5 flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <p className="text-sm text-gray-600">
                        Results for{' '}
                        <strong>
                          {responseData.input_type === 'cutoff'
                            ? `cutoff ${responseData.user_cutoff}`
                            : `rank ${responseData.user_rank?.toLocaleString()}`}
                        </strong>{' '}
                        · <strong>{responseData.community}</strong>
                      </p>
                      <div className="flex gap-3 mt-1 text-xs">
                        <span className="text-green-700 font-semibold">✓ {qualified.length} qualify</span>
                        {nearMiss.length > 0 && (
                          <button
                            onClick={() => document.getElementById('near-miss-v2')?.scrollIntoView({ behavior: 'smooth' })}
                            className="text-orange-600 font-semibold hover:underline"
                          >
                            ⚠ {nearMiss.length} just short ↓
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      ↑ Edit search
                    </button>
                  </div>

                  {/* Active filter pills */}
                  {(formData.department || formData.districts.length > 0) && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {formData.department && (
                        <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-100">
                          {branches.find(b => b.branch_code === formData.department)?.display_name || formData.department}
                        </span>
                      )}
                      {formData.districts.length > 0 && (
                        <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-100">
                          {formData.districts.map(id => districts.find(d => d.id.toString() === id)?.name).filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Branch filter — single scrollable row */}
                  {uniqueBranches.length > 1 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-400 mb-2">Filter by branch:</p>
                      <div className="relative flex items-center gap-1">
                        {/* Left arrow — desktop only */}
                        <button
                          onClick={() => scrollBranches('left')}
                          className="hidden md:flex flex-shrink-0 items-center justify-center w-7 h-7 rounded-full border border-gray-200 bg-white text-gray-500 hover:text-blue-600 hover:border-blue-400 shadow-sm transition-colors"
                          aria-label="Scroll branches left"
                        >
                          <FaChevronLeft className="text-xs" />
                        </button>
                        <div
                          ref={branchScrollRef}
                          className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide flex-1"
                          style={{ scrollbarWidth: 'none' }}
                        >
                          {/* All — clears selection */}
                          <button
                            onClick={() => setBranchFilters([])}
                            className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${branchFilters.length === 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                          >
                            All ({allQualified.length})
                          </button>
                          {uniqueBranches.map(branch => {
                            const isSelected = branchFilters.includes(branch);
                            return (
                              <button
                                key={branch}
                                onClick={() => toggleBranch(branch)}
                                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                              >
                                {branch} ({allQualified.filter((p: any) => p.course.branch_name === branch).length})
                              </button>
                            );
                          })}
                        </div>
                        {/* Right arrow — desktop only */}
                        <button
                          onClick={() => scrollBranches('right')}
                          className="hidden md:flex flex-shrink-0 items-center justify-center w-7 h-7 rounded-full border border-gray-200 bg-white text-gray-500 hover:text-blue-600 hover:border-blue-400 shadow-sm transition-colors"
                          aria-label="Scroll branches right"
                        >
                          <FaChevronRight className="text-xs" />
                        </button>
                      </div>
                      {/* Selected count hint */}
                      {branchFilters.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1.5">
                          {branchFilters.length} branch{branchFilters.length > 1 ? 'es' : ''} selected
                          <button onClick={() => setBranchFilters([])} className="ml-2 underline hover:text-blue-800">Clear</button>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Qualified list */}
                  {qualified.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        Colleges You Qualify For ({qualified.length})
                      </h3>
                      <div className="space-y-2">
                        {qualified.slice(0, visibleQualified).map((p: any, i: number) => (
                          <PredictionCard
                            key={`q-${i}`}
                            prediction={p}
                            inputType={inputType}
                            userValue={userValue}
                            selected={selectedForComparison.includes(p.college.id)}
                            onCompare={() => handleCompareToggle(p.college.id)}
                            onChoiceList={() => addToChoiceList({ id: p.college.id, college_code: p.college.college_code, college_name: p.college.college_name, college_type: p.college.college_type, branch_code: p.course.branch_code, branch_name: p.course.branch_name, naac_grade: p.college.naac_grade, district: p.college.district, annual_tuition_fee: p.college.annual_tuition_fee })}
                            inChoiceList={isInChoiceList(p.college.college_code, p.course.branch_code)}
                          />
                        ))}
                      </div>
                      {qualified.length > visibleQualified && (
                        <button
                          onClick={() => setVisibleQualified(v => v + 20)}
                          className="mt-4 w-full py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          Show {Math.min(20, qualified.length - visibleQualified)} more ({qualified.length - visibleQualified} remaining)
                        </button>
                      )}
                    </div>
                  )}

                  {/* Near-miss list */}
                  {nearMiss.length > 0 && (
                    <div id="near-miss-v2" className="mb-8">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Just Short — Worth a Try ({nearMiss.length})
                      </h3>
                      <p className="text-xs text-orange-700 mb-3">{responseData.disclaimer}</p>
                      <div className="space-y-2">
                        {nearMiss.slice(0, visibleNearMiss).map((p: any, i: number) => (
                          <PredictionCard
                            key={`nm-${i}`}
                            prediction={p}
                            inputType={inputType}
                            userValue={userValue}
                            selected={selectedForComparison.includes(p.college.id)}
                            onCompare={() => handleCompareToggle(p.college.id)}
                            onChoiceList={() => addToChoiceList({ id: p.college.id, college_code: p.college.college_code, college_name: p.college.college_name, college_type: p.college.college_type, branch_code: p.course.branch_code, branch_name: p.course.branch_name, naac_grade: p.college.naac_grade, district: p.college.district, annual_tuition_fee: p.college.annual_tuition_fee })}
                            inChoiceList={isInChoiceList(p.college.college_code, p.course.branch_code)}
                          />
                        ))}
                      </div>
                      {nearMiss.length > visibleNearMiss && (
                        <button
                          onClick={() => setVisibleNearMiss(v => v + 10)}
                          className="mt-4 w-full py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          Show {Math.min(10, nearMiss.length - visibleNearMiss)} more ({nearMiss.length - visibleNearMiss} remaining)
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {!predictMutation.isPending && !predictMutation.data && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm text-center py-16">
                <FaRocket className="text-5xl text-gray-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-500 mb-1">Ready to Predict?</h3>
                <p className="text-sm text-gray-400">Enter your details to get personalised college predictions.</p>
              </div>
            )}
          </div>
        </div>

        {/* Floating scroll-up */}
        {showScrollTop && predictMutation.isSuccess && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-24 md:bottom-8 right-6 md:right-8 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all z-50 flex items-center gap-2 px-4 py-2.5"
          >
            <span className="text-base leading-none">↑</span>
            <span className="text-sm font-medium">New Search</span>
          </button>
        )}

        {/* Sticky comparison bar */}
        {selectedForComparison.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-xl p-3 z-[45] pb-[calc(0.75rem+64px)] md:pb-3">
            <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
                <span className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full font-bold text-sm whitespace-nowrap">
                  {selectedForComparison.length} Selected
                </span>
                {(() => {
                  const rd = (predictMutation.data as any)?.data || (predictMutation.data as any);
                  const all = [...(rd?.qualified || []), ...(rd?.near_miss || [])];
                  const unique = all.filter((p: any) => selectedForComparison.includes(p.college.id))
                    .reduce((acc: any[], p: any) => acc.some(c => c.college.id === p.college.id) ? acc : [...acc, p], []);
                  return unique.map((p: any) => (
                    <span key={p.college.id} className="inline-flex items-center gap-1.5 bg-gray-100 px-2.5 py-1 rounded-full text-xs whitespace-nowrap">
                      {p.college.college_name.split(',')[0].split(' ').slice(0, 3).join(' ')}
                      <button onClick={() => handleCompareToggle(p.college.id)} className="text-gray-400 hover:text-red-500">×</button>
                    </span>
                  ));
                })()}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setSelectedForComparison([])}
                  className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={handleCompare}
                  disabled={selectedForComparison.length < 2}
                  className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Compare →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CollegePredictor;
